import type { AcceptMappings, Label } from './types';

// Wildcard MIME type mappings
const DEFAULT_WILDCARDS: Record<string, Label> = {
  'image/*': 'Images',
  'audio/*': 'Audio',
  'video/*': 'Video',
  'text/*': 'Text',
  'application/*': 'Documents',
};

// MIME type mappings
const DEFAULT_MIMES: Record<string, Label> = {
  // Images
  'image/jpeg': 'JPEG',
  'image/jpg': 'JPEG',
  'image/png': 'PNG',
  'image/gif': 'GIF',
  'image/webp': 'WEBP',
  'image/svg+xml': 'SVG',
  'image/bmp': 'BMP',
  'image/tiff': 'TIFF',
  'image/avif': 'AVIF',
  'image/heic': 'HEIC',
  'image/heif': 'HEIC',

  // Documents
  'application/pdf': 'PDF',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
    'Word',
  'application/vnd.ms-excel': 'Excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
  'application/vnd.ms-powerpoint': 'PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation':
    'PowerPoint',
  'application/rtf': 'Rich Text',
  'text/plain': 'Text',
  'text/csv': 'CSV',
  'text/markdown': 'Markdown',
  'text/x-markdown': 'Markdown',
  'application/json': 'JSON',
  'application/xml': 'XML',
  'text/xml': 'XML',
  'text/html': 'HTML',
  'application/vnd.oasis.opendocument.text': 'OpenDocument Text',
  'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument Spreadsheet',
  'application/vnd.oasis.opendocument.presentation':
    'OpenDocument Presentation',

  // Archives
  'application/zip': 'ZIP',
  'application/x-zip-compressed': 'ZIP',
  'application/x-7z-compressed': '7Z',
  'application/x-rar-compressed': 'RAR',
  'application/vnd.rar': 'RAR',
  'application/x-tar': 'TAR',
  'application/gzip': 'GZIP',
  'application/x-gzip': 'GZIP',
  'application/x-bzip2': 'BZIP2',

  // Audio
  'audio/mpeg': 'MP3',
  'audio/mp3': 'MP3',
  'audio/aac': 'AAC',
  'audio/aacp': 'AAC',
  'audio/wav': 'WAV',
  'audio/wave': 'WAV',
  'audio/x-wav': 'WAV',
  'audio/flac': 'FLAC',
  'audio/ogg': 'OGG',
  'audio/opus': 'OPUS',
  'audio/m4a': 'M4A',
  'audio/mp4': 'M4A',
  'audio/webm': 'WEBM',

  // Video
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/x-msvideo': 'AVI',
  'video/x-matroska': 'MKV',
  'video/webm': 'WEBM',
  'video/3gpp': '3GP',
  'video/3gpp2': '3G2',
  'video/mpeg': 'MPEG',
  'video/ogg': 'OGV',

  // Adobe and publishing
  'application/postscript': 'PostScript',
  'application/illustrator': 'Illustrator',
  'application/vnd.adobe.illustrator': 'Illustrator',
  'image/vnd.adobe.photoshop': 'Photoshop',
  'application/epub+zip': 'EPUB',
};

// Extension mappings
const DEFAULT_EXTS: Record<string, Label> = {
  // Images
  jpg: 'JPEG',
  jpeg: 'JPEG',
  jpe: 'JPEG',
  png: 'PNG',
  gif: 'GIF',
  webp: 'WEBP',
  avif: 'AVIF',
  bmp: 'BMP',
  svg: 'SVG',
  tif: 'TIFF',
  tiff: 'TIFF',
  heic: 'HEIC',
  heif: 'HEIC',
  ico: 'ICO',

  // Documents
  pdf: 'PDF',
  doc: 'Word',
  docx: 'Word',
  xls: 'Excel',
  xlsx: 'Excel',
  ppt: 'PowerPoint',
  pptx: 'PowerPoint',
  rtf: 'Rich Text',
  txt: 'Text',
  csv: 'CSV',
  md: 'Markdown',
  json: 'JSON',
  xml: 'XML',
  html: 'HTML',
  htm: 'HTML',
  odt: 'OpenDocument Text',
  ods: 'OpenDocument Spreadsheet',
  odp: 'OpenDocument Presentation',
  pages: 'Pages',
  numbers: 'Numbers',
  key: 'Keynote',
  keynote: 'Keynote',

  // Archives
  zip: 'ZIP',
  '7z': '7Z',
  rar: 'RAR',
  tar: 'TAR',
  gz: 'GZIP',
  gzip: 'GZIP',
  bz2: 'BZIP2',

  // Audio
  mp3: 'MP3',
  aac: 'AAC',
  wav: 'WAV',
  flac: 'FLAC',
  ogg: 'OGG',
  opus: 'OPUS',
  m4a: 'M4A',
  oga: 'OGG',
  weba: 'WEBM',

  // Video
  mp4: 'MP4',
  mov: 'MOV',
  avi: 'AVI',
  mkv: 'MKV',
  webm: 'WEBM',
  '3gp': '3GP',
  '3g2': '3G2',
  mpeg: 'MPEG',
  mpg: 'MPEG',
  ogv: 'OGV',

  // Adobe and publishing
  ai: 'Illustrator',
  psd: 'Photoshop',
  eps: 'PostScript',
  epub: 'EPUB',
};

// Combined mappings object
export const DEFAULT_MAPPINGS: AcceptMappings = {
  wildcards: DEFAULT_WILDCARDS,
  mimes: DEFAULT_MIMES,
  exts: DEFAULT_EXTS,
};

// Fallback messages
export const DEFAULT_FILE_MESSAGE =
  'Drag and drop your JPG, PNG, or PDF files here, or browse for files';
